import CreditScoreIcon from '@mui/icons-material/CreditScore';
import { Stack } from '@mui/material';
import { useCreditLineRequests } from 'lib/api/creditLine';
import {
  formatDateString,
  formatMoney,
  getShortString,
} from 'lib/functions/utils';
import {
  BottomDock,
  BoxItemStack,
  ContentCard,
  FilterDock,
  FilterGroup,
  type FilterPillOption,
  FilterPills,
  getCreditLineStatusChipType,
  getCreditLineStatusLabel,
  PaginationDock,
  StatusChipType,
} from 'lib/mobileDesignSystem';
import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { buildPaginatedQuery } from '../../lib/queryUtils';

const PAGE_SIZE = 10;

const STATUS_OPTIONS: FilterPillOption[] = [
  { label: 'No solicitada', value: 'NOT_REQUESTED' },
  { label: 'Validando', value: 'VALIDATING' },
  { label: 'Revisada', value: 'REVIEWED' },
  { label: 'Asignada', value: 'ASSIGNED' },
  { label: 'Activa', value: 'ACTIVE' },
  { label: 'Rechazada', value: 'REJECTED' },
  { label: 'Congelada', value: 'FROZEN' },
  { label: 'Vencida', value: 'DUE' },
];

const CreditLineRequests: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('cl_page') ?? '1');
  const appliedStatuses =
    searchParams.get('cl_statuses')?.split(',').filter(Boolean) ?? [];

  const [searchStatuses, setSearchStatuses] = useState<FilterPillOption[]>(() =>
    appliedStatuses.map(
      (v) =>
        STATUS_OPTIONS.find((o) => o.value === v) ?? { label: v, value: v },
    ),
  );

  const setPage = (newPage: number) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('cl_page', String(newPage));
        return next;
      },
      { replace: true },
    );
  };

  const {
    data: response = {
      data: [],
      pagination: { page: 1, pageSize: PAGE_SIZE, total: 0, totalPages: 0 },
    },
    isLoading,
    isFetching,
  } = useCreditLineRequests(
    dispatch,
    buildPaginatedQuery({
      page,
      pageSize: PAGE_SIZE,
      filters: { status: appliedStatuses },
    }),
  );

  const handleSearch = useCallback(() => {
    const newStatuses = searchStatuses.map((s) => s.value);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('cl_statuses', newStatuses.join(','));
        next.set('cl_page', '1');
        return next;
      },
      { replace: true },
    );
  }, [searchStatuses, setSearchParams]);

  const items = response.data.map((item) => ({
    primaryText: getShortString(item.businessName, 20),
    amount: formatMoney(item.amount),
    secondaryText:
      formatDateString(item.createdAt) +
      ' · ' +
      getShortString(item.executives.map((e) => e.name).join(', '), 40),
    chips: [
      {
        text: item.id.toString(),
        type: 'info' as StatusChipType,
      },
      {
        text: item.productName,
        type: 'info' as StatusChipType,
      },
      {
        text: getCreditLineStatusLabel(item.status),
        type: getCreditLineStatusChipType(item.status),
      },
    ],
    onClick: () => navigate(`/finance/credit-line/${item.id}`),
  }));

  return (
    <Stack spacing={2} sx={{ pb: 10 }}>
      <ContentCard
        header={{
          icon: <CreditScoreIcon sx={{ fontSize: 20 }} />,
          title: 'Todas las solicitudes',
          description: 'Gestiona tus solicitudes de línea de crédito.',
        }}
      >
        <BoxItemStack items={items} loading={isLoading} skeletonCount={10} />
      </ContentCard>
      <BottomDock
        center={
          <PaginationDock
            page={page}
            totalPages={response.pagination?.totalPages}
            onPageChange={setPage}
            loading={isFetching}
          />
        }
        right={
          <FilterDock title="Filtros" onSearch={handleSearch}>
            <FilterGroup label="Estados">
              <FilterPills
                options={STATUS_OPTIONS}
                value={searchStatuses}
                onChange={setSearchStatuses}
              />
            </FilterGroup>
          </FilterDock>
        }
      />
    </Stack>
  );
};

export default CreditLineRequests;
